// A published board (spec §17.2): anyone with the link can look, read-only
// and live, without an account. No names, cursors or history; a "Made with
// Atomic Lab" bar on Free; a way to report it.
import { REPORT_LABELS, REPORT_REASONS, type PublicBoardInfo } from '../../shared/api.ts';
import type { BoardObject } from '../../shared/types.ts';
import { ApiError, get, post } from '../api.ts';
import { Editor } from '../editor.ts';
import { attachInput } from '../input.ts';
import { BoardSync } from '../sync/board-sync.ts';
import { h } from '../util/dom.ts';
import { logoMark } from './logo.ts';
import { createMinimap } from './minimap.ts';
import { busy, centered, field, formError, mount, PRODUCT, select, setTitle } from './page.ts';
import { createZoomControls } from './toolbar.ts';

const POLL_MS = 30_000;

export async function publicBoardPage(token: string): Promise<void> {
  let info: PublicBoardInfo;
  try {
    info = await get<PublicBoardInfo>(`/api/public/${encodeURIComponent(token)}`, { redirectOn401: false });
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err;
    return notPublished();
  }
  setTitle(info.title);
  document.body.classList.add('board-mode', 'public-board');

  const container = h('main', { id: 'board', 'aria-label': `${info.title}, a board on ${PRODUCT}` });
  const banner = h('div', { class: 'banner', role: 'alert', hidden: '' });
  mount(container, titleBar(), banner, ...(info.badge ? [badge()] : []));

  const editor = new Editor(container, { readOnly: true });
  editor.images.query = `?p=${encodeURIComponent(token)}`; // what lets the public see the board's images
  editor.audio.query = editor.images.query; // and play its audio (spec §19.2)
  editor.audio.onError = (message) => showBanner(message);
  attachInput(editor);
  editor.setTool('hand'); // dragging pans: there's nothing to select or edit
  document.body.append(createZoomControls(editor), createMinimap(editor));

  let polling = 0;
  const sync = new BoardSync(editor, token, 'public', {
    publicToken: token,
    onFirstLoad: () => editor.zoomToFit(),
    onKicked: (reason) => {
      if (reason === 'busy') return void poll(); // too many people watching live: refresh every 30 s instead
      clearInterval(polling);
      container.classList.add('locked');
      showBanner('This board is no longer published.');
    },
  });
  sync.start();
  if (__DEV__) Object.assign(window as object, { editor, sync });

  async function poll(): Promise<void> {
    let first = true;
    const load = async () => {
      try {
        const snap = await get<{ objects: BoardObject[] }>(`/api/public/${encodeURIComponent(token)}/snapshot`, { redirectOn401: false });
        editor.loadObjects(snap.objects);
        if (first) editor.zoomToFit();
        first = false;
      } catch {
        clearInterval(polling);
        showBanner('This board is no longer published.');
      }
    };
    await load();
    polling = window.setInterval(() => void load(), POLL_MS);
    showBanner('Lots of people are viewing this board, so it refreshes every 30 seconds.');
  }

  function showBanner(message: string): void {
    banner.replaceChildren(h('span', {}, [message]));
    banner.hidden = false;
  }

  function titleBar(): HTMLElement {
    const report = h('button', { type: 'button', class: 'btn small subtle' }, ['Report']);
    report.addEventListener('click', () => openReport(token));
    return h('div', { class: 'panel board-bar public-bar' }, [
      h('a', { href: '/', class: 'tb-btn public-home', title: PRODUCT, 'aria-label': `${PRODUCT} home` }, [logoMark(20)]),
      h('span', { class: 'public-title' }, [info.title]),
      h('span', { class: 'badge' }, ['View only']),
      report,
    ]);
  }
}

/** Free boards carry a link back (spec §17.2), tagged so sign-ups are credited to public boards. */
function badge(): HTMLElement {
  return h('a', { class: 'made-with', href: '/?utm_source=public-board&utm_medium=badge' }, [
    logoMark(16), `Made with ${PRODUCT}`, h('span', { class: 'made-with-cta' }, ['Try it free']),
  ]);
}

function notPublished(): void {
  setTitle("This board isn't published");
  mount(centered(
    h('h1', {}, ["This board isn't published"]),
    h('p', { class: 'muted' }, ['The link may have been turned off or replaced. Ask whoever shared it for a new one.']),
    h('a', { class: 'btn primary', href: '/' }, [`Go to ${PRODUCT}`]),
  ));
}

/** "Report this board" (spec §17.4). */
function openReport(token: string): void {
  const reason = select(REPORT_REASONS.map((r) => [r, REPORT_LABELS[r]]), 'illegal', { 'aria-label': 'Reason' });
  const details = h('textarea', { rows: '4', maxlength: '2000', placeholder: 'What’s wrong with it?' });
  const contact = h('input', { type: 'email', autocomplete: 'email', placeholder: 'you@example.com (optional)' });
  const send = h('button', { type: 'submit', class: 'btn primary' }, ['Send report']);
  const cancel = h('button', { type: 'button', class: 'btn' }, ['Cancel']);
  const error = formError();
  const form = h('form', { class: 'stack' }, [
    h('p', { class: 'muted' }, ['Tell us what’s wrong, and we’ll look at it. Reports are anonymous unless you leave an email address.']),
    field('Reason', reason), field('Details', details), field('Your email', contact, 'Only if you’d like a reply.'),
    error, h('div', { class: 'dialog-actions' }, [cancel, send]),
  ]);
  const dialog = h('dialog', { class: 'dialog', 'aria-labelledby': 'report-title' }, [h('h2', { id: 'report-title' }, ['Report this board']), form]);
  cancel.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.clear();
    void busy(send, async () => {
      try {
        await post(`/api/public/${encodeURIComponent(token)}/report`, { reason: reason.value, details: details.value, contact: contact.value }, { redirectOn401: false });
        form.replaceChildren(h('p', {}, ['Thanks. We’ll look at it.']), h('div', { class: 'dialog-actions' }, [cancel]));
        cancel.textContent = 'Close';
      } catch (err) {
        error.show(err);
      }
    });
  });
  document.body.append(dialog);
  dialog.showModal();
}
